import { makeAutoObservable, runInAction } from "mobx"
import { getCachedEntity, setCachedEntity, clearCachedIDBByEntityKey, IDBEntityCacheOptions, updateCachedEntity } from "./IDBEntityCache"
import userCache from "../userCache"
import { GapError } from "@trpc-endpoints/trpc/error-handler/GapError"
import { Sentry } from "sentry/sentry"

interface IDBEntityCacheStoreProps<T> {
   entityKey: string
   fetcher: () => Promise<T | Response>
   cacheOptions?: IDBEntityCacheOptions
   updater?: (data: T) => Promise<void>
}

export class IDBEntityCacheStore<T = any[]> {
   data: T | null = null
   loading = false
   error: any = null
   sp

   constructor(sp: IDBEntityCacheStoreProps<T>) {
      makeAutoObservable(this)
      this.sp = sp
   }

   // Check if data is set in cache
   get isSet() {
      return getCachedEntity<T>(this.sp.entityKey, this.sp.cacheOptions)
   }

   get isServiceUser() {
      return userCache.serviceUser.get()
   }

   async update(data: T) {
      this.loading = true
      await updateCachedEntity(this.sp.entityKey, JSON.parse(JSON.stringify(data)))
      try {
         if (this.sp.updater) {
            await this.sp.updater(data)
         }
         runInAction(() => {
            this.data = data
            this.loading = false
         })
      } catch (error: any) {
         runInAction(() => {
            this.error = error
            this.loading = false
         })
      }
   }

   async fetch(forceRefresh = false): Promise<T | null> {
      this.loading = true
      this.error = null
      try {
         if (this.isServiceUser) {
            // If we are a service user, check if this is already set in cache
            const serviceUserCache = window.localStorage.getItem(`serviceUser.${this.sp.entityKey}`)
            if (serviceUserCache) {
               this.data = JSON.parse(serviceUserCache) as T
               this.loading = false
               return this.data
            }
         }
         if (!forceRefresh) {
            const cached = await getCachedEntity<T>(this.sp.entityKey, this.sp.cacheOptions)
            if (cached) {
               runInAction(() => {
                  this.data = cached
                  this.loading = false
               })
               return this.data
            }
         }
         let fresh = await this.sp.fetcher()

         if (dataIsFetchResponse(fresh) && 'json' in fresh && typeof fresh.json === 'function') {
            // If we are using the fetch method, we need to parse the response
            try {
               const convertedData = await fresh.json()
               fresh = convertedData
            } catch (error: any) {
               console.error('Error converting fetch response to JSON', error)
               Sentry.reportCustomMessage('Error converting fetch response to JSON', {
                  extra: {
                     error,
                     entityKey: this.sp.entityKey
                  }
               })
            }
         }

         await setCachedEntity(this.sp.entityKey, fresh, this.sp.cacheOptions)
         runInAction(() => {
            this.data = fresh as T
            this.loading = false
         })
         return this.data
      } catch (error: any) {
         runInAction(() => {
            this.error = error
            this.loading = false
         })
         if (error?.data?.gapError?.message === GapError.messages.forbidden.noDocumentAccessUser) {
            throw error
         }
         return null
      }
   }

   async invalidate() {
      await clearCachedIDBByEntityKey(this.sp.entityKey)
      this.data = null
   }
}

function dataIsFetchResponse<T>(value: T): value is T extends Response ? T : Response & T {
   return value instanceof Response
}